import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { toast } from "sonner";
import { Pencil, Plus, Trash2 } from "lucide-react";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { AppHeader } from "@/components/AppHeader";
import { StaffDialog, PERMS } from "@/components/StaffDialog";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";

const StaffCard = ({ s, books, onEdit, onDelete }) => (
  <div className="bg-white border border-slate-300 p-5 fade-up" data-testid={`staff-card-${s.id}`}>
    <div className="flex items-start justify-between gap-3">
      <div className="min-w-0">
        <p className="font-extrabold text-lg tracking-tight truncate" data-testid="staff-card-name">{s.name}</p>
        <p className="text-xs text-slate-500 font-mono truncate">{s.email}</p>
      </div>
      <div className="flex items-center gap-1 shrink-0">
        <span className={`text-[10px] font-bold uppercase tracking-[0.12em] px-2 py-1 ${s.active ? "bg-green-100 text-green-800" : "bg-slate-200 text-slate-600"}`} data-testid="staff-card-status">
          {s.active ? "Active" : "Off"}
        </span>
        <button data-testid={`staff-edit-${s.id}`} onClick={onEdit} className="h-8 w-8 grid place-items-center hover:bg-slate-900 hover:text-white transition-colors"><Pencil size={14} /></button>
        <button data-testid={`staff-delete-${s.id}`} onClick={onDelete} className="h-8 w-8 grid place-items-center text-red-700 hover:bg-red-50 transition-colors"><Trash2 size={14} /></button>
      </div>
    </div>
    <div className="flex flex-wrap gap-1.5 mt-4">
      {PERMS.filter((p) => s.permissions[p.k]).map((p) => (
        <span key={p.k} className="text-[11px] font-semibold bg-slate-100 border border-slate-200 px-2 py-0.5">{p.l}</span>
      ))}
      {!PERMS.some((p) => s.permissions[p.k]) && <span className="text-[11px] text-slate-500">View only</span>}
    </div>
    <p className="text-xs text-slate-600 mt-3">
      <span className="font-semibold">Books:</span>{" "}
      {s.book_ids.length ? books.filter((b) => s.book_ids.includes(b.id)).map((b) => b.name).join(", ") : <span className="text-red-700">none assigned</span>}
    </p>
  </div>
);

export default function StaffPage() {
  const { isAdmin } = useAuth();
  const [staff, setStaff] = useState(null);
  const [books, setBooks] = useState([]);
  const [dlg, setDlg] = useState({ open: false, staff: null });
  const [del, setDel] = useState(null);

  const load = () => api.get("/staff").then((r) => setStaff(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => {
    if (!isAdmin) return;
    load();
    api.get("/books").then((r) => setBooks(r.data)).catch(() => {});
  }, [isAdmin]);

  if (!isAdmin) return <Navigate to="/" replace />;

  const remove = async () => {
    try {
      await api.delete(`/staff/${del.id}`);
      toast.success("Staff removed");
      setDel(null);
      load();
    } catch (ex) {
      toast.error(errMsg(ex));
    }
  };

  return (
    <div className="min-h-screen grid-paper">
      <AppHeader />
      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-10 sm:py-14">
        <div className="flex items-end justify-between gap-4 mb-10 fade-up">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-500 mb-2">Admin</p>
            <h1 className="text-4xl sm:text-5xl font-extrabold tracking-tight">Staff</h1>
          </div>
          <button data-testid="add-staff-btn" onClick={() => setDlg({ open: true, staff: null })}
            className="h-11 px-4 bg-slate-900 text-white font-semibold flex items-center gap-2 hover:bg-slate-800 transition-colors">
            <Plus size={16} /> Add staff
          </button>
        </div>
        {staff === null && <p className="text-sm text-slate-500" data-testid="staff-loading">Loading…</p>}
        {staff?.length === 0 && (
          <div className="border border-dashed border-slate-400 bg-white/60 p-10 text-center" data-testid="staff-empty">
            <p className="font-semibold">No staff yet</p>
            <p className="text-sm text-slate-500 mt-1">Add a staff login and choose which books and actions they get.</p>
          </div>
        )}
        <div className="grid sm:grid-cols-2 gap-4">
          {staff?.map((s) => (
            <StaffCard key={s.id} s={s} books={books} onEdit={() => setDlg({ open: true, staff: s })} onDelete={() => setDel(s)} />
          ))}
        </div>
      </main>
      <StaffDialog open={dlg.open} onOpenChange={(o) => setDlg((d) => ({ ...d, open: o }))} staff={dlg.staff} books={books} onSaved={load} />
      <AlertDialog open={Boolean(del)} onOpenChange={(o) => !o && setDel(null)}>
        <AlertDialogContent className="rounded-none">
          <AlertDialogHeader>
            <AlertDialogTitle>Remove {del?.name}?</AlertDialogTitle>
            <AlertDialogDescription>They won't be able to sign in. Entries they made stay in the books.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-none">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="staff-delete-confirm" onClick={remove} className="rounded-none bg-red-700 hover:bg-red-800">Remove</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
